(function () {
  "use strict";

  var catalog = Array.isArray(window.MINI_MELTS_ORDER_CATALOG)
    ? window.MINI_MELTS_ORDER_CATALOG
    : [];

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function formatMoney(value) {
    if (value == null || value === "") return "Price pending";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(value));
  }

  /** ISO date from <input type="date"> -> US month/day/year for display. */
  function formatDate(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || "").trim());
    return match ? match[2] + "/" + match[3] + "/" + match[1] : value;
  }

  /**
   * Normalize the optional date-picker value for both the app proxy and the
   * native Shopify contact form. Native date inputs already return ISO, while
   * some theme date-picker versions write the visible US date instead.
   */
  function normalizeNeededBy(value) {
    var raw = String(value == null ? "" : value).trim();
    if (!raw || /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

    var match = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(raw);
    if (!match) return raw;

    var first = Number(match[1]);
    var second = Number(match[2]);
    var year = Number(match[3]);
    // The live theme is configured for US month/day/year. Also accept a
    // day-first value when the first component cannot be a month.
    var month = first > 12 && second <= 12 ? second : first;
    var day = first > 12 && second <= 12 ? first : second;
    if (month < 1 || month > 12 || day < 1 || day > 31) return raw;

    var candidate = new Date(Date.UTC(year, month - 1, day));
    if (
      candidate.getUTCFullYear() !== year ||
      candidate.getUTCMonth() !== month - 1 ||
      candidate.getUTCDate() !== day
    ) {
      return raw;
    }

    return (
      String(year).padStart(4, "0") +
      "-" +
      String(month).padStart(2, "0") +
      "-" +
      String(day).padStart(2, "0")
    );
  }

  /**
   * US phone display format: (555) 123-4567. Below four digits it returns the
   * bare digits so backspacing never hits a wall of punctuation it can't
   * delete. A leading "+" means the visitor is typing an international
   * number, so leave it exactly as entered.
   */
  function formatPhone(value) {
    var raw = String(value == null ? "" : value);
    if (raw.trim().charAt(0) === "+") return raw;
    var digits = raw.replace(/\D/g, "");
    if (digits.length > 10 && digits.charAt(0) === "1") digits = digits.slice(1);
    digits = digits.slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return "(" + digits.slice(0, 3) + ") " + digits.slice(3);
    return "(" + digits.slice(0, 3) + ") " + digits.slice(3, 6) + "-" + digits.slice(6);
  }

  function debounce(fn, delay) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, delay);
    };
  }

  // CSS prefers-reduced-motion cannot override an explicit smooth behavior
  // passed to scrollIntoView, so honor the preference here.
  function scrollBehavior() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth";
  }

  /**
   * Scroll an element into view. Some environments accept a smooth request and
   * then never animate, leaving the page where it was, so fall back to an
   * instant jump if nothing has moved shortly after.
   */
  function scrollToElement(element, block) {
    if (!element) return;
    var behavior = scrollBehavior();
    var before = window.pageYOffset;
    element.scrollIntoView({ behavior: behavior, block: block || "start" });
    if (behavior !== "smooth") return;
    setTimeout(function () {
      if (window.pageYOffset === before) {
        element.scrollIntoView({ behavior: "auto", block: block || "start" });
      }
    }, 350);
  }

  // The catalog stores bare CDN URLs; sizing is requested at render time.
  // Product-image URLs arrive in two shapes: cdn.shopify.com file URLs (from
  // the import CSV / preview fixture) and the storefront's proxied
  // /cdn/shop/ paths emitted by Liquid's image_url — both accept ?width=.
  function cdnImage(url, width) {
    if (!url) return url;
    if (url.indexOf("cdn.shopify.com") === -1 && url.indexOf("/cdn/shop/") === -1) return url;
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
  }

  function init(root) {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    var sectionId = root.dataset.sectionId || "default";
    var storageKey = "mini-melts-request:" + sectionId + ":v2";
    var requestIdKey = "mini-melts-request-id:" + sectionId + ":v2";
    var productsEl = root.querySelector("[data-mm-products]");
    var categoriesEl = root.querySelector("[data-mm-categories]");
    var searchEl = root.querySelector("[data-mm-search]");
    // Optional: the availability toggle was removed from the default markup.
    // Absent means "hide unavailable items", which is the intended behaviour.
    var showUnavailableEl = root.querySelector("[data-mm-show-unavailable]");
    var resultCountEl = root.querySelector("[data-mm-result-count]");
    var selectionEl = root.querySelector("[data-mm-selection]");
    var selectedCountEl = root.querySelector("[data-mm-selected-count]");
    var totalEl = root.querySelector("[data-mm-total]");
    var totalNoteEl = root.querySelector("[data-mm-total-note]");
    var promoInputEl = root.querySelector("[data-mm-promo-code]");
    var promoButtonEl = root.querySelector("[data-mm-apply-promo]");
    var promoStatusEl = root.querySelector("[data-mm-promo-status]");
    var discountRowEl = root.querySelector("[data-mm-discount-row]");
    var discountValueEl = root.querySelector("[data-mm-discount-value]");
    var alertEl = root.querySelector("[data-mm-form-alert]");
    var orderBodyEl = root.querySelector("[data-mm-order-body]");
    var draftOrderEl = root.querySelector("[data-mm-draft-order]");
    var contactPromoEl = root.querySelector("[data-mm-contact-promo]");
    var contactTotalEl = root.querySelector("[data-mm-contact-total]");
    var clearEl = root.querySelector("[data-mm-clear]");
    var form = root.querySelector("form");
    var submitButton = root.querySelector("[data-mm-submit]");
    var submitLabelEl = submitButton ? submitButton.querySelector("[data-mm-submit-label]") : null;
    var submitLabelText = submitLabelEl ? submitLabelEl.textContent : "";
    var honeypotEl = root.querySelector("[data-mm-honeypot]");
    var previewSuccess = root.querySelector("[data-mm-preview-success]");
    var postedStatusEl = root.querySelector("[data-mm-posted-successfully]");
    var appProxyPath = String(root.dataset.mmAppProxy || "").trim().replace(/\/$/, "");
    var activeCategory = "All";
    var appliedPromotion = null;
    var submitting = false;

    // On the post-submit page Shopify renders only the confirmation, so none
    // of the catalog elements exist. Handle that before the catalog guard
    // below, or a failed catalog load would leave the restart button dead.
    var thanksEl = root.querySelector("[data-mm-thanks]");
    var postedNow = postedStatusEl && postedStatusEl.dataset.mmPostedSuccessfully === "true";
    if (postedNow && thanksEl && !thanksEl.hidden) {
      showThanks(thanksEl);
      return;
    }
    if (thanksEl) wireRestart(thanksEl);

    if (!catalog.length) {
      // A stray ?page=N URL makes the Liquid pagination emit an empty
      // catalog; retry once at the canonical URL before showing the error.
      try {
        if (/[?&]page=/.test(window.location.search) && !sessionStorage.getItem("mm-page-retry")) {
          sessionStorage.setItem("mm-page-retry", "1");
          window.location.replace(window.location.pathname);
          return;
        }
      } catch (error) {
        // Storage unavailable — fall through to the visible error state.
      }
      // Without catalog data the form would email an empty request, so
      // surface the failure and keep the submit button disabled.
      if (productsEl) {
        productsEl.innerHTML =
          '<div class="mm-order__empty mm-order__empty--error" role="alert">' +
          "The product list could not be loaded. Please refresh the page or contact us directly.</div>";
      }
      if (resultCountEl) resultCountEl.textContent = "0 items";
      if (submitButton) submitButton.disabled = true;
      return;
    }

    var quantities = loadQuantities();

    // Drop saved selections that no longer exist or went out of stock, and
    // clamp the rest, so a refreshed catalog can't strand un-removable items.
    Object.keys(quantities).forEach(function (key) {
      var product = getProduct(key);
      var max = product ? Math.max(0, Number(product.stock) || 0) : 0;
      var quantity = Math.floor(Number(quantities[key]) || 0);
      if (max <= 0 || quantity <= 0) delete quantities[key];
      else quantities[key] = Math.min(quantity, max);
    });
    persistQuantities();

    if (postedStatusEl && postedStatusEl.dataset.mmPostedSuccessfully === "true") {
      quantities = {};
      persistQuantities();
    }

    /** Clear the saved basket, surface the confirmation, wire the button. */
    function showThanks(element) {
      try {
        localStorage.removeItem(storageKey);
        sessionStorage.removeItem(requestIdKey);
      } catch (error) {
        // Nothing to clean up if storage is unavailable.
      }

      // Shopify appends the contact form's #anchor to the success URL, which
      // drops the visitor partway down the page. Strip it before scrolling.
      if (window.location.hash) {
        try {
          history.replaceState(null, "", window.location.pathname + window.location.search);
        } catch (error) {
          // Older browsers keep the fragment; the retries below still correct it.
        }
      }

      // The browser's own fragment jump and any theme scroll restoration can
      // land after this runs, so re-assert the position once the page settles.
      revealThanks(element);
      window.addEventListener("load", function () { revealThanks(element); }, { once: true });
      setTimeout(function () { revealThanks(element); }, 300);

      element.focus({ preventScroll: true });
      wireRestart(element);
    }

    /**
     * Put the whole confirmation card on screen rather than just its top edge.
     * Centering it when it fits keeps it clear of sticky theme headers; taller
     * cards fall back to top alignment, where the CSS scroll-margin applies.
     */
    function revealThanks(element) {
      var box = element.getBoundingClientRect();
      var fits = box.height + 96 < window.innerHeight;
      element.scrollIntoView({ behavior: "auto", block: fits ? "center" : "start" });
    }

    /** "Submit another request" returns to a clean URL, dropping ?contact_posted. */
    function wireRestart(element) {
      var button = element.querySelector("[data-mm-restart]");
      if (!button || button.dataset.wired === "true") return;
      button.dataset.wired = "true";
      button.addEventListener("click", function () {
        window.location.href = window.location.pathname;
      });
    }

    function loadQuantities() {
      try {
        var parsed = JSON.parse(localStorage.getItem(storageKey) || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (error) {
        return {};
      }
    }

    function persistQuantities() {
      try {
        localStorage.setItem(storageKey, JSON.stringify(quantities));
      } catch (error) {
        // The form remains fully usable if browser storage is unavailable.
      }
    }

    function getProduct(key) {
      return catalog.find(function (product) {
        return product.key === key;
      });
    }

    function categories() {
      var unique = [];
      catalog.forEach(function (product) {
        if (unique.indexOf(product.category) === -1) unique.push(product.category);
      });
      return ["All"].concat(unique);
    }

    function renderCategories() {
      categoriesEl.innerHTML = categories()
        .map(function (category) {
          var count = category === "All"
            ? catalog.length
            : catalog.filter(function (product) { return product.category === category; }).length;
          return (
            '<button type="button" class="mm-order__category" data-mm-category="' +
            escapeHtml(category) + '" aria-pressed="' + (category === activeCategory) + '">' +
            escapeHtml(category) + " (" + count + ")</button>"
          );
        })
        .join("");
    }

    function filteredProducts() {
      var query = searchEl.value.trim().toLowerCase();
      var showUnavailable = showUnavailableEl ? showUnavailableEl.checked : false;
      return catalog.filter(function (product) {
        var categoryMatch = activeCategory === "All" || product.category === activeCategory;
        var searchText = [product.sku, product.name, product.category, product.logo]
          .join(" ")
          .toLowerCase();
        var searchMatch = !query || searchText.indexOf(query) !== -1;
        var availabilityMatch = showUnavailable || product.stock > 0;
        return categoryMatch && searchMatch && availabilityMatch;
      });
    }

    function productImage(product) {
      if (!product.image) {
        return (
          '<div class="mm-order__placeholder"><span class="mm-order__placeholder-mark"></span>' +
          "No image</div>"
        );
      }
      return (
        '<img class="mm-order__image" src="' + escapeHtml(cdnImage(product.image, 96)) +
        '" srcset="' + escapeHtml(cdnImage(product.image, 96)) + " 1x, " +
        escapeHtml(cdnImage(product.image, 192)) + ' 2x" alt="' +
        escapeHtml(product.name) + '" width="82" height="82" loading="lazy" decoding="async" data-mm-product-image>' +
        '<div class="mm-order__placeholder" data-mm-image-placeholder hidden>' +
        '<span class="mm-order__placeholder-mark"></span>No image</div>'
      );
    }

    function renderProducts() {
      var visible = filteredProducts();
      resultCountEl.textContent = visible.length + (visible.length === 1 ? " item" : " items");
      if (!visible.length) {
        productsEl.innerHTML = '<div class="mm-order__empty">No products match those filters.</div>';
        return;
      }

      productsEl.innerHTML = visible
        .map(function (product) {
          var quantity = Number(quantities[product.key] || 0);
          var unavailable = product.stock <= 0;
          var selected = quantity > 0;
          var max = unavailable ? 0 : product.stock;
          var name = escapeHtml(product.name);
          return (
            '<article class="mm-order__product" data-product-key="' + escapeHtml(product.key) +
            '" data-selected="' + selected + '" data-unavailable="' + unavailable + '">' +
              '<div class="mm-order__image-wrap">' + productImage(product) + "</div>" +
              '<div class="mm-order__product-copy">' +
                '<p class="mm-order__sku">' + escapeHtml(product.sku) + "</p>" +
                '<h3 class="mm-order__product-name">' + name + "</h3>" +
                '<div class="mm-order__meta">' +
                  "<span>" + escapeHtml(product.category) + "</span>" +
                  (product.logo ? "<span>Logo: " + escapeHtml(product.logo) + "</span>" : "") +
                  '<span class="mm-order__stock" data-empty="' + unavailable + '">' +
                    (unavailable
                      ? "Currently unavailable"
                      : product.stock >= 9999
                        ? "In stock"
                        : product.stock + " in stock") +
                  "</span>" +
                "</div>" +
              "</div>" +
              '<div class="mm-order__product-actions">' +
                '<span class="mm-order__price" data-pending="' + (product.price == null) + '">' +
                  formatMoney(product.price) +
                "</span>" +
                '<div class="mm-order__qty" role="group" aria-label="Quantity for ' + name + '">' +
                  '<button type="button" class="mm-order__qty-button" data-mm-quantity-action="decrease" ' +
                    'data-key="' + escapeHtml(product.key) + '" aria-label="Decrease quantity for ' + name + '" ' +
                    (unavailable ? "disabled" : "") + ">−</button>" +
                  '<input class="mm-order__qty-input" data-mm-quantity data-key="' + escapeHtml(product.key) +
                    '" type="number" inputmode="numeric" min="0" max="' + max + '" value="' + quantity +
                    '" aria-label="Quantity for ' + name + '" ' + (unavailable ? "disabled" : "") + ">" +
                  '<button type="button" class="mm-order__qty-button" data-mm-quantity-action="increase" ' +
                    'data-key="' + escapeHtml(product.key) + '" aria-label="Increase quantity for ' + name + '" ' +
                    (unavailable ? "disabled" : "") + ">+</button>" +
                "</div>" +
                '<span class="mm-order__qty-hint" data-mm-qty-hint hidden></span>' +
              "</div>" +
            "</article>"
          );
        })
        .join("");

      productsEl.querySelectorAll("[data-mm-product-image]").forEach(function (image) {
        image.addEventListener("error", function () {
          if (!image.dataset.mmImageRetry && image.src.indexOf("width=") !== -1) {
            // Retry once without the CDN size transform before giving up.
            image.dataset.mmImageRetry = "true";
            image.removeAttribute("srcset");
            image.src = image.src.split("?")[0];
            return;
          }
          image.hidden = true;
          var placeholder = image.parentElement.querySelector("[data-mm-image-placeholder]");
          if (placeholder) placeholder.hidden = false;
        });
      });
    }

    /**
     * Enlarged hover preview of a product photo. Pointer devices only — on
     * touch there is no hover, and a tap should still just work the row.
     * The panel is fixed-position and appended to <body> because the product
     * list scrolls, which would clip anything positioned inside it.
     */
    var previewEl = null;
    var canHover = !window.matchMedia || window.matchMedia("(hover: hover)").matches;

    function hidePreview() {
      if (previewEl) previewEl.dataset.visible = "false";
    }

    function showPreview(image) {
      if (!canHover || !image || !image.src) return;
      if (!previewEl) {
        previewEl = document.createElement("figure");
        previewEl.className = "mm-order-preview";
        previewEl.setAttribute("aria-hidden", "true");
        previewEl.innerHTML = '<img alt=""><figcaption></figcaption>';
        document.body.appendChild(previewEl);
      }
      var card = image.closest("[data-product-key]");
      var product = card && getProduct(card.dataset.productKey);
      var full = previewEl.querySelector("img");
      // Ask the CDN for a larger rendition than the 82px thumbnail.
      var source = image.getAttribute("src").split("?")[0];
      full.src = cdnImage(source, 640);
      full.alt = product ? product.name : "";
      previewEl.querySelector("figcaption").textContent = product
        ? product.sku + " · " + product.name
        : "";

      previewEl.dataset.visible = "true";
      positionPreview(image);
      // The panel is caption-height until the larger rendition arrives, so
      // place it again once the real dimensions are known.
      if (!full.complete) {
        full.addEventListener("load", function () {
          if (previewEl && previewEl.dataset.visible === "true") positionPreview(image);
        }, { once: true });
      }
    }

    function positionPreview(image) {
      if (!previewEl) return;
      var anchorRect = image.getBoundingClientRect();
      var box = previewEl.getBoundingClientRect();
      var margin = 12;

      // Sit clear of the catalog panel rather than on top of it, so the row's
      // own name, price and stock stay readable while the photo is open.
      var panel = productsEl.closest("[data-mm-catalog-panel]") || productsEl;
      var panelRect = panel.getBoundingClientRect();
      var left = panelRect.right + margin;
      if (left + box.width > window.innerWidth - margin) {
        // No room beside the panel: tuck it left of the thumbnail instead.
        left = anchorRect.left - box.width - margin;
      }
      if (left < margin) {
        left = Math.min(anchorRect.right + margin, window.innerWidth - box.width - margin);
      }
      left = Math.max(margin, left);

      // Vertically follow the row being hovered, clamped to the viewport.
      var top = anchorRect.top + anchorRect.height / 2 - box.height / 2;
      top = Math.min(Math.max(margin, top), window.innerHeight - box.height - margin);

      previewEl.style.left = Math.round(left) + "px";
      previewEl.style.top = Math.round(top) + "px";
    }

    if (canHover) {
      productsEl.addEventListener("mouseover", function (event) {
        var image = event.target.closest("[data-mm-product-image]");
        if (image && !image.hidden) showPreview(image);
      });
      productsEl.addEventListener("mouseout", function (event) {
        var image = event.target.closest("[data-mm-product-image]");
        var to = event.relatedTarget;
        if (image && (!to || !to.closest || !to.closest("[data-mm-product-image]"))) hidePreview();
      });
      // The list scrolls under the cursor, so drop the panel rather than
      // leaving it pinned to a thumbnail that has moved away.
      productsEl.addEventListener("scroll", hidePreview, { passive: true });
      window.addEventListener("scroll", hidePreview, { passive: true });
    }

    /**
     * The Mini Melts theme replaces <input type="date"> with its own
     * datepicker widget configured dd-mm-yyyy, which is why the field showed
     * day/month/year. Re-point that widget at US month/day/year once it has
     * initialized — it loads after us, so poll briefly rather than assume.
     *
     * A no-op where the theme has no picker: the native control then follows
     * the viewer's locale and submits ISO, which formatDate() normalizes for
     * the email and the log.
     */
    function useUsDateFormat() {
      var field = root.querySelector("[name='contact[needed_by]']");
      if (!field) return;
      var attempts = 0;
      (function apply() {
        var picker = field.datepicker;
        if (picker && typeof picker.setOptions === "function") {
          try {
            picker.setOptions({ format: "mm/dd/yyyy" });
          } catch (error) {
            // Leave the theme's default rather than break the field.
          }
          return;
        }
        if (++attempts < 25) setTimeout(apply, 120);
      })();
    }

    // Update a single card in place. A quantity change never affects filter
    // visibility, so a full re-render (which would destroy focus on the
    // control being used) is unnecessary.
    /** Format the phone number as it is typed. */
    function wirePhoneFormat() {
      var field = root.querySelector("[name='contact[phone]']");
      if (!field) return;
      field.addEventListener("input", function () {
        // Rewriting the value parks the caret at the end, so only reformat
        // while the visitor is typing there. Mid-string edits are left alone.
        if (field.selectionStart !== field.value.length) return;
        var next = formatPhone(field.value);
        if (next === field.value) return;
        field.value = next;
        try {
          field.setSelectionRange(next.length, next.length);
        } catch (error) {
          // Some browsers refuse setSelectionRange on tel inputs; harmless.
        }
      });
      field.addEventListener("blur", function () {
        field.value = formatPhone(field.value);
      });
    }

    function updateProductCard(key, quantity, clamped, options) {
      var card = productsEl.querySelector('[data-product-key="' + key + '"]');
      if (!card) return;
      card.dataset.selected = quantity > 0 ? "true" : "false";
      var input = card.querySelector("[data-mm-quantity]");
      if (input && (options.normalizeField !== false || clamped) && input.value !== String(quantity)) {
        input.value = quantity;
      }
      var hint = card.querySelector("[data-mm-qty-hint]");
      if (hint) {
        clearTimeout(Number(card.dataset.mmHintTimer || 0));
        if (clamped) {
          hint.textContent = "Only " + quantity + " in stock";
          hint.hidden = false;
          card.dataset.mmHintTimer = setTimeout(function () {
            hint.hidden = true;
          }, 2500);
        } else {
          hint.hidden = true;
        }
      }
    }

    function selectedProducts() {
      return catalog
        .map(function (product) {
          return { product: product, quantity: Number(quantities[product.key] || 0) };
        })
        .filter(function (entry) { return entry.quantity > 0; });
    }

    function renderSelection() {
      var selected = selectedProducts();
      var unitCount = selected.reduce(function (sum, entry) { return sum + entry.quantity; }, 0);
      var pricedTotal = selected.reduce(function (sum, entry) {
        return entry.product.price == null
          ? sum
          : sum + Number(entry.product.price) * entry.quantity;
      }, 0);
      var pendingCount = selected.filter(function (entry) { return entry.product.price == null; }).length;

      selectedCountEl.textContent = unitCount + (unitCount === 1 ? " unit" : " units");
      totalEl.textContent = appliedPromotion
        ? formatMoney(appliedPromotion.total)
        : pricedTotal > 0
          ? formatMoney(pricedTotal)
          : pendingCount
            ? "Pending pricing"
            : formatMoney(0);
      totalNoteEl.textContent = appliedPromotion
        ? "Shopify merchandise total after promo. Shipping and taxes are handled through SAP."
        : pendingCount
          ? "Excludes " + pendingCount + (pendingCount === 1 ? " item" : " items") + " with pricing pending. Final availability and pricing require approval."
          : "The estimated total excludes shipping and handling. Pricing and product availability are subject to confirmation upon submission.";
      if (discountRowEl && discountValueEl) {
        discountRowEl.hidden = !appliedPromotion;
        discountValueEl.textContent = appliedPromotion
          ? "-" + formatMoney(appliedPromotion.discount)
          : "-" + formatMoney(0);
      }
      clearEl.hidden = selected.length === 0;

      if (!selected.length) {
        selectionEl.innerHTML = '<li class="mm-order__selection-empty">Choose quantities to build your request.</li>';
        return;
      }

      selectionEl.innerHTML = selected
        .map(function (entry) {
          return (
            '<li class="mm-order__selection-item">' +
              "<div><strong>" + escapeHtml(entry.product.sku) + " · " +
                escapeHtml(entry.product.name) + "</strong><span>Qty " + entry.quantity +
                " · " + formatMoney(entry.product.price) + "</span></div>" +
              '<button type="button" class="mm-order__selection-remove" data-mm-remove="' +
                escapeHtml(entry.product.key) + '">Remove</button>' +
            "</li>"
          );
        })
        .join("");
    }

    function setQuantity(key, value, options) {
      options = options || {};
      var product = getProduct(key);
      if (!product) return;
      var max = Math.max(0, Number(product.stock) || 0);
      var requested = Math.floor(Number(value) || 0);
      var next = Math.max(0, Math.min(max, requested));
      if (next > 0) quantities[key] = next;
      else delete quantities[key];
      invalidatePromotion(appliedPromotion ? "Items changed. Apply the promo code again to refresh the total." : "");
      persistQuantities();
      updateProductCard(key, next, requested > max && max > 0, options);
      renderSelection();
      alertEl.dataset.visible = "false";
    }

    // One id per page visit: retries use the same value so the server can
    // return the original Shopify draft rather than create a duplicate.
    var pendingRequestId = null;

    function ensureRequestId() {
      if (!pendingRequestId) {
        try {
          pendingRequestId = sessionStorage.getItem(requestIdKey);
        } catch (error) {
          pendingRequestId = null;
        }
      }
      if (!pendingRequestId) {
        pendingRequestId = requestId();
        try {
          sessionStorage.setItem(requestIdKey, pendingRequestId);
        } catch (error) {
          // Idempotency still works within this page when storage is blocked.
        }
      }
      return pendingRequestId;
    }

    function fieldValue(selector) {
      var element = root.querySelector(selector);
      return element ? String(element.value || "").trim() : "";
    }

    function selectedFingerprint() {
      return selectedProducts().map(function (entry) {
        return entry.product.variantId + ":" + entry.quantity;
      }).join("|");
    }

    function invalidatePromotion(message) {
      appliedPromotion = null;
      if (discountRowEl) discountRowEl.hidden = true;
      if (promoStatusEl) {
        promoStatusEl.textContent = message || "";
        promoStatusEl.dataset.state = message ? "info" : "";
      }
    }

    function normalizedPromoCode() {
      return promoInputEl ? promoInputEl.value.trim().toUpperCase() : "";
    }

    function normalizedNeededByField() {
      var field = form.querySelector("[name='contact[needed_by]']");
      if (!field) return "";
      var normalized = normalizeNeededBy(field.value);
      // Keep the native contact form in sync with the value sent to the app.
      // Blank remains blank because the field is optional.
      if (field.value !== normalized) field.value = normalized;
      return normalized;
    }

    function buildPayload() {
      return {
        request_id: ensureRequestId(),
        submitted_at: new Date().toISOString(),
        page_url: window.location.href,
        promo_code: normalizedPromoCode(),
        honeypot: honeypotEl ? honeypotEl.value : "",
        customer: {
          name: fieldValue("[name='contact[name]']"),
          email: fieldValue("[name='contact[email]']"),
          phone: fieldValue("[name='contact[phone]']"),
          company: fieldValue("[name='contact[company]']"),
        },
        shipping_address: {
          address1: fieldValue("[name='contact[address1]']"),
          address2: fieldValue("[name='contact[address2]']"),
          city: fieldValue("[name='contact[city]']"),
          province: fieldValue("[name='contact[state]']"),
          zip: fieldValue("[name='contact[zip]']"),
          country_code: "US",
        },
        needed_by: normalizedNeededByField(),
        notes: fieldValue("[name='contact[request_notes]']"),
        items: selectedProducts().map(function (entry) {
          return {
            variant_id: entry.product.variantId,
            quantity: entry.quantity,
          };
        }),
      };
    }

    function proxyEndpoint(route) {
      if (!/^\/apps\/[A-Za-z0-9_-]+$/.test(appProxyPath)) {
        throw new Error("The draft-order service is not configured yet.");
      }
      return appProxyPath + "/" + route;
    }

    function requestJson(route, payload) {
      return fetch(proxyEndpoint(route), {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(payload),
      }).then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (body) {
          if (!response.ok || !body.ok) {
            var message = body && body.error && body.error.message
              ? body.error.message
              : "Shopify could not process this request. Please try again.";
            throw new Error(message);
          }
          return body;
        });
      });
    }

    function applyPromotion() {
      var code = normalizedPromoCode();
      if (!code) {
        invalidatePromotion("");
        renderSelection();
        if (promoStatusEl) {
          promoStatusEl.textContent = "Enter a promo code to apply.";
          promoStatusEl.dataset.state = "error";
        }
        return Promise.resolve();
      }
      if (!selectedProducts().length) {
        if (promoStatusEl) {
          promoStatusEl.textContent = "Choose at least one product before applying a promo code.";
          promoStatusEl.dataset.state = "error";
        }
        return Promise.resolve();
      }
      if (root.dataset.preview === "true") {
        if (promoStatusEl) {
          promoStatusEl.textContent = "Promo codes are validated by Shopify when this v6 form is connected.";
          promoStatusEl.dataset.state = "info";
        }
        return Promise.resolve();
      }

      if (promoButtonEl) promoButtonEl.disabled = true;
      if (promoStatusEl) {
        promoStatusEl.textContent = "Checking with Shopify…";
        promoStatusEl.dataset.state = "info";
      }
      var fingerprint = selectedFingerprint();
      return requestJson("calculate", buildPayload()).then(function (body) {
        if (fingerprint !== selectedFingerprint()) {
          invalidatePromotion("Items changed. Apply the promo code again to refresh the total.");
          return;
        }
        appliedPromotion = {
          code: body.promotion.code,
          subtotal: Number(body.totals.subtotal),
          discount: Number(body.totals.discount),
          total: Number(body.totals.total),
          currency: body.totals.currency,
        };
        if (promoInputEl) promoInputEl.value = body.promotion.code;
        if (promoStatusEl) {
          promoStatusEl.textContent = "Promo code applied.";
          promoStatusEl.dataset.state = "success";
        }
        renderSelection();
      }).catch(function (error) {
        invalidatePromotion("");
        renderSelection();
        if (promoStatusEl) {
          promoStatusEl.textContent = error.message;
          promoStatusEl.dataset.state = "error";
        }
      }).finally(function () {
        if (promoButtonEl) promoButtonEl.disabled = false;
      });
    }

    function requestId() {
      try {
        if (window.crypto && typeof window.crypto.randomUUID === "function") {
          return window.crypto.randomUUID();
        }
      } catch (error) {
        // Fall through to the timestamp-based id below.
      }
      return "req-" + Date.now() + "-" + Math.random().toString(16).slice(2, 10);
    }

    function buildOrderBody(result) {
      var selected = selectedProducts();
      var street = fieldValue("[name='contact[address1]']");
      var unit = fieldValue("[name='contact[address2]']");
      var lines = [
        "MINI MELTS POS DRAFT ORDER REQUEST",
        "",
        "Shopify draft: " + result.draft.name,
        "Request ID: " + ensureRequestId(),
        "Requested by: " + fieldValue("[name='contact[name]']"),
        "Email: " + fieldValue("[name='contact[email]']"),
        "Phone: " + (fieldValue("[name='contact[phone]']") || "Not provided"),
        "Business Name or Depot: " + fieldValue("[name='contact[company]']"),
        "Needed by: " + (formatDate(fieldValue("[name='contact[needed_by]']")) || "Not specified"),
        "",
        "SHIP TO",
        street + (unit ? ", " + unit : ""),
        fieldValue("[name='contact[city]']")
          + ", " + fieldValue("[name='contact[state]']")
          + " " + fieldValue("[name='contact[zip]']"),
        "",
        "REQUESTED ITEMS",
      ];
      selected.forEach(function (entry, index) {
        lines.push(
          (index + 1) + ". " + entry.product.sku + " | " + entry.product.name,
          "   Category: " + entry.product.category + (entry.product.logo ? " | Logo: " + entry.product.logo : ""),
          "   Quantity: " + entry.quantity
        );
      });

      lines.push(
        "",
        "Promo code: " + (result.promotion ? result.promotion.code : "None"),
        "Shopify merchandise subtotal: " + formatMoney(result.totals.subtotal),
        "Promo discount: -" + formatMoney(result.totals.discount),
        "Shopify merchandise total: " + formatMoney(result.totals.total),
        "Inventory reserved until: " + result.draft.reserve_inventory_until,
        "",
        "WHAT CUSTOMER OR USE CASE IS THIS POS INTENDED FOR?",
        fieldValue("[name='contact[request_notes]']") || "None",
        "",
        "This Shopify draft is awaiting staff approval. SAP remains the source for shipping, taxes, invoicing, and payment."
      );
      return lines.join("\n");
    }

    categoriesEl.addEventListener("click", function (event) {
      var button = event.target.closest("[data-mm-category]");
      if (!button) return;
      activeCategory = button.dataset.mmCategory;
      renderCategories();
      renderProducts();
    });

    searchEl.addEventListener("input", debounce(renderProducts, 150));
    if (showUnavailableEl) showUnavailableEl.addEventListener("change", renderProducts);

    productsEl.addEventListener("click", function (event) {
      var button = event.target.closest("[data-mm-quantity-action]");
      if (!button) return;
      var key = button.dataset.key;
      var current = Number(quantities[key] || 0);
      setQuantity(key, current + (button.dataset.mmQuantityAction === "increase" ? 1 : -1));
    });

    // Live totals while typing; leave the field un-normalized until change
    // fires so the caret isn't fought mid-edit.
    productsEl.addEventListener("input", function (event) {
      var input = event.target.closest("[data-mm-quantity]");
      if (!input || input.value === "") return;
      setQuantity(input.dataset.key, input.value, { normalizeField: false });
    });

    productsEl.addEventListener("change", function (event) {
      var input = event.target.closest("[data-mm-quantity]");
      if (input) setQuantity(input.dataset.key, input.value);
    });

    selectionEl.addEventListener("click", function (event) {
      var button = event.target.closest("[data-mm-remove]");
      if (button) setQuantity(button.dataset.mmRemove, 0);
    });

    if (promoInputEl) {
      promoInputEl.addEventListener("input", function () {
        if (appliedPromotion && normalizedPromoCode() !== appliedPromotion.code.toUpperCase()) {
          invalidatePromotion("Promo code changed. Apply it again to refresh the total.");
          renderSelection();
        }
      });
      promoInputEl.addEventListener("keydown", function (event) {
        if (event.key !== "Enter") return;
        event.preventDefault();
        applyPromotion();
      });
    }
    if (promoButtonEl) promoButtonEl.addEventListener("click", applyPromotion);

    var jumpEl = root.querySelector("[data-mm-jump-to-form]");
    if (jumpEl) {
      jumpEl.addEventListener("click", function () {
        var details = root.querySelector(".mm-order__details");
        if (!details) return;
        scrollToElement(details, "start");
        // Move focus to the first field so keyboard users land there too.
        var first = details.querySelector("input, textarea");
        if (first) first.focus({ preventScroll: true });
      });
    }

    clearEl.addEventListener("click", function () {
      quantities = {};
      invalidatePromotion("");
      persistQuantities();
      renderProducts();
      renderSelection();
    });

    // Shopify's hCaptcha provider adds its own submit listener and can invoke
    // the native form while the draft request is still pending. Capture the
    // event first so that path cannot send an incomplete staff alert.
    form.addEventListener("submit", function (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (submitting) return;
      if (honeypotEl && honeypotEl.value) {
        return;
      }

      var selected = selectedProducts();
      if (!selected.length) {
        alertEl.textContent = "Select at least one available product before sending your request.";
        alertEl.dataset.visible = "true";
        alertEl.focus({ preventScroll: true });
        scrollToElement(alertEl, "center");
        return;
      }

      if (root.dataset.preview === "true") {
        var body = root.querySelector("[data-mm-form-body]");
        if (body) body.hidden = true;
        previewSuccess.hidden = false;
        showThanks(previewSuccess);
        return;
      }

      submitting = true;
      alertEl.dataset.visible = "false";
      submitButton.disabled = true;
      if (submitLabelEl) submitLabelEl.textContent = "Submitting request…";

      requestJson("submit", buildPayload()).then(function (result) {
        if (draftOrderEl) draftOrderEl.value = result.draft.name;
        if (contactPromoEl) contactPromoEl.value = result.promotion ? result.promotion.code : "None";
        if (contactTotalEl) contactTotalEl.value = formatMoney(result.totals.total);
        orderBodyEl.value = buildOrderBody(result);
        if (submitLabelEl) submitLabelEl.textContent = "Submitting request…";
        // The draft already exists. Let Shopify's hCaptcha wrapper (when
        // present) run now that the hidden alert fields are complete; on an
        // unbound form this calls the native submit directly.
        form.submit();
      }).catch(function (error) {
        submitting = false;
        submitButton.disabled = false;
        if (submitLabelEl) submitLabelEl.textContent = submitLabelText;
        alertEl.textContent = error.message;
        alertEl.dataset.visible = "true";
        alertEl.focus({ preventScroll: true });
        scrollToElement(alertEl, "center");
      });
    }, true);

    // Restore the submit button when the page is served from the back/forward
    // cache, which otherwise revives it stuck in the submitting state.
    window.addEventListener("pageshow", function (event) {
      if (!event.persisted || !submitButton) return;
      submitting = false;
      submitButton.disabled = false;
      if (submitLabelEl) submitLabelEl.textContent = submitLabelText;
    });

    useUsDateFormat();
    wirePhoneFormat();
    renderCategories();
    renderProducts();
    renderSelection();
  }

  function boot() {
    document.querySelectorAll("[data-mm-order-root]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
